import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FilesService } from 'api';
import { fetchSpreadsheetData } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/util';
import { SpreadsheetNoteData } from 'components/SpreadsheetNotes';
import { XLSX_SPREADSHEET_NOTES_VERSION as XLSX_VERSION, xlsxSpreadsheetNotesQueryKey, xlsxSpreadsheetNotesQueryOptions } from 'services/hooks/xlsxSpreadsheetNotesQuery';
import logger from 'utilities/logger';

const MAX_CACHE_SIZE = 10 * 1024 * 1024; // 10MB limit
const MAX_POLLING_ATTEMPTS = 30;
const POLLING_TIMEOUT = 60 * 1000 * 5; // 5 minutes

const estimateDataSize = (data: any) => {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    // Fallback estimation
    return JSON.stringify(data).length * 2; // Rough estimate
  }
};

type PollingState = 'idle' | 'polling' | 'success' | 'completed-without-success';

export const useXlsxSpreadsheetNotesQuery = (
  documentFileId?: number | string,
) => {


  const [pollingState, setPollingState] = useState<PollingState>('idle');
  const [pollingStartTime, setPollingStartTime] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const fileId = documentFileId?.toString();
  const [shouldPoll, setShouldPoll] = useState(false);

  // Reset all states when documentFileId changes
  useEffect(() => {
    setPollingState('idle');
    setPollingStartTime(null);
    setShouldPoll(false);
    setFailureCount(0);
  }, [documentFileId]);


  // Main data query
  const {
    data: mainData,
    error: mainError,
    isLoading: isMainLoading,
  } = useQuery<SpreadsheetNoteData[]>({
    ...xlsxSpreadsheetNotesQueryOptions(fileId),
    enabled: Boolean(fileId),
  });

  const [failureCount, setFailureCount] = useState(0);

  // Start polling
  useEffect(() => {
    if (mainError && !mainData && fileId && pollingState === 'idle') {
      setPollingState('polling');
      setPollingStartTime(Date.now());
      setShouldPoll(true);
    }
  }, [mainError, mainData, fileId, pollingState]);

  // Polling query - controlled by shouldPoll state
  const {
    data: pollingData,
    isLoading: isPollingLoading,
    isSuccess: isPollingSuccess,
  } = useQuery<SpreadsheetNoteData[]>({
    queryKey: ['xlsxPolling', XLSX_VERSION, fileId],
    queryFn: async () => {
      if (!fileId) {
        throw new Error('Document file ID is missing');
      }
      try {
        const response = await FilesService.apiFilesProcessedRead(fileId, false);
        if (response.message === 'File has not been processed yet') {
          throw new Error(response.message); // Re-throw the error to keep retrying
        }
        const presignedUrl = response.url;
        const data = await fetchSpreadsheetData(presignedUrl);

        const dataSize = estimateDataSize(data);
        if (dataSize > MAX_CACHE_SIZE) {
          logger.info(`Large spreadsheet data from polling: ${Math.round(dataSize / 1024 / 1024)}MB - not caching`);
        }

        return data;
      } catch (error) {
        // Increment the failure count if polling fails
        setFailureCount(prevCount => prevCount + 1);
        throw error; // Re-throw the error to keep retrying
      }
    },
    enabled: shouldPoll, // Simple boolean control
    refetchInterval: shouldPoll ? 10000 : false, // Only poll when shouldPoll is true
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });

  // Share a processed workbook with viewer and Push consumers before stopping polling.
  useEffect(() => {
    if (pollingState === 'polling') {
      if (isPollingSuccess && pollingData) {
        setPollingState('success');
        setShouldPoll(false);

        const dataSize = estimateDataSize(pollingData);

        if (dataSize <= MAX_CACHE_SIZE) {
          queryClient.setQueryData(xlsxSpreadsheetNotesQueryKey(fileId), pollingData);
        }
        queryClient.cancelQueries({ queryKey: ['xlsxPolling', XLSX_VERSION, fileId] });

      } else if (failureCount >= MAX_POLLING_ATTEMPTS) {
        setPollingState('completed-without-success');
        setShouldPoll(false);
        logger.info(`Polling ran full course but never succeeded for file ${fileId}`);
      }
    }
  }, [pollingState, isPollingSuccess, pollingData, failureCount, fileId, queryClient]);

  // Timeout fallback
  useEffect(() => {
    if (pollingState === 'polling' && pollingStartTime) {
      const timeoutId = setTimeout(() => {
        if (pollingState === 'polling') {
          setPollingState('completed-without-success');
          setShouldPoll(false);
        }
      }, POLLING_TIMEOUT);

      return () => clearTimeout(timeoutId);
    }
  }, [pollingState, pollingStartTime]);

  return {
    data: mainData || pollingData,
    //   isLoading if either the main is loading or we are polling and the polling is loading
    isLoading: isMainLoading || (pollingState === 'polling' && isPollingLoading),
    //   is an error if the mainError has failed and we are not polling and we hae no pollingData
    error: (mainData || pollingData || pollingState === 'polling') ? null : mainError,
    isPolling: pollingState === 'polling',
  };
};
